import axios from 'axios';
import { api } from './api';

export interface LoginCredentials {
  userId: string;
  password: string;
  verifyCode: string;
  captchaId?: string;
}

export interface AuthResponse {
  token: string;
  refreshToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    type: string;
    priceLevel: string;
    traderCode?: string;
    emailVerified?: boolean;
  };
}

export interface RegisterInput {
  userId: string;
  email: string;
  name: string;
  password: string;
  // Required since #131: delivery photos are sent to customers over WhatsApp.
  handPhone: string;
}

export interface GuestSession {
  guestId: string;
  sessionToken: string;
  priceLevel: string;
  isGuest: true;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  errors?: any[];
}

class AuthService {
  private getLoginErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const responseMessage = (error.response?.data as { message?: string } | undefined)?.message?.trim();

      if (status === 400) {
        if (responseMessage?.toLowerCase().includes('verification code')) {
          return 'Verification code is incorrect or expired. Please try again.';
        }

        return responseMessage || 'Please check your sign-in details and try again.';
      }

      if (status === 401) {
        return 'Invalid User ID or Password';
      }

      if (status === 403) {
        return 'Account is locked or disabled. Please contact support.';
      }

      if (status && status >= 500) {
        return 'Sign-in service unavailable. Please try again.';
      }

      if (responseMessage) {
        return responseMessage;
      }
    }

    return error instanceof Error ? error.message : 'Login failed';
  }

  /**
   * User login
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials, {
        skipAuthRefresh: true,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Login failed');
      }

      return response.data;
    } catch (error) {
      throw new Error(this.getLoginErrorMessage(error));
    }
  }

  /**
   * Self-service registration. Returns an auth session (auto-login); the account
   * is usable immediately but email is unverified until the emailed link is clicked.
   */
  async register(input: RegisterInput): Promise<{ auth: AuthResponse; message: string }> {
    try {
      const response = await api.post<AuthResponse>('/auth/register', input, { skipAuthRefresh: true });
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Registration failed');
      }
      return { auth: response.data, message: response.message };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg = (error.response?.data as { message?: string } | undefined)?.message;
        throw new Error(msg || 'Registration failed');
      }
      throw new Error(error instanceof Error ? error.message : 'Registration failed');
    }
  }

  /** Verify an email token; returns how many past guest orders were linked. */
  async verifyEmail(token: string): Promise<{ claimedOrders: number; message: string }> {
    const response = await api.post<{ email: string; claimedOrders: number }>(
      '/auth/verify-email', { token }, { skipAuthRefresh: true }
    );
    if (!response.success) {
      throw new Error(response.message || 'Verification failed');
    }
    return { claimedOrders: response.data?.claimedOrders ?? 0, message: response.message };
  }

  /** Resend the verification email (always succeeds; no account enumeration). */
  async resendVerification(email: string): Promise<void> {
    await api.post('/auth/resend-verification', { email }, { skipAuthRefresh: true });
  }

  /** Request a password-reset email (always succeeds; no account enumeration). */
  async forgotPassword(email: string): Promise<string> {
    const response = await api.post<void>('/auth/forgot-password', { email }, { skipAuthRefresh: true });
    return response.message || 'If an account exists for that email, a reset link has been sent.';
  }

  /** Reset the password with a token from the reset email. */
  async resetPassword(token: string, newPassword: string): Promise<string> {
    try {
      const response = await api.post<void>('/auth/reset-password', { token, newPassword }, { skipAuthRefresh: true });
      if (!response.success) throw new Error(response.message || 'Password reset failed');
      return response.message || 'Your password has been reset.';
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg = (error.response?.data as { message?: string } | undefined)?.message;
        throw new Error(msg || 'Password reset failed');
      }
      throw new Error(error instanceof Error ? error.message : 'Password reset failed');
    }
  }

  /**
   * Create guest session for checkout
   */
  async createGuestSession(): Promise<GuestSession> {
    const response = await api.post<GuestSession>('/auth/guest', undefined, {
      skipAuthRefresh: true,
    });
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to create guest session');
    }
    
    return response.data;
  }

  /**
   * Refresh JWT token
   */
  async refreshToken(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
    const response = await api.post<{ token: string; refreshToken: string }>(
      '/auth/refresh',
      { refreshToken },
      { skipAuthRefresh: true }
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Token refresh failed');
    }
    
    return response.data;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if server request fails
      console.warn('Logout request failed:', error);
    }
  }

  /**
   * Get user profile
   */
  async getProfile(): Promise<any> {
    const response = await api.get<{ user: any; traderInfo: any }>('/auth/profile');
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to get profile');
    }
    
    return response.data;
  }

  /**
   * Update user profile.
   *
   * Returns the server's message rather than swallowing it: changing the email
   * drops a self-registered account back to unverified (issue #119), and the
   * customer has to be told that on the spot.
   */
  async updateProfile(updates: { name?: string; email?: string; phone?: string }): Promise<string> {
    const response = await api.put<void>('/auth/profile', updates);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to update profile');
    }

    return response.message || 'Profile updated successfully';
  }

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await api.post<void>('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to change password');
    }
  }

  /**
   * Get verification code (CAPTCHA)
   */
  async getVerificationCode(): Promise<{ captchaId: string; imageUrl: string; expiresAt: Date }> {
    const response = await api.get<{ captchaId: string; imageUrl: string; expiresAt: string }>(
      '/auth/verify-code',
      undefined,
      { skipAuthRefresh: true }
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to get verification code');
    }
    
    return {
      captchaId: response.data.captchaId,
      imageUrl: response.data.imageUrl,
      expiresAt: new Date(response.data.expiresAt)
    };
  }

  /**
   * Verify token validity
   */
  async verifyToken(): Promise<{ user: any }> {
    const response = await api.get<{ user: any }>('/auth/verify');
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Invalid token');
    }
    
    return response.data;
  }
}

export const authService = new AuthService();
